/* TanStack Query hooks over the approver-facing accounting-api routes
   (/v1/expense/approvals). The inbox lists reports routed to the caller for
   approval; a decision approves/rejects one. SoD + routing (only the assigned
   approver, never the requester) are enforced SERVER-SIDE in the ApprovalEngine —
   these hooks just call the API. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { expenseFetch } from '@/expense/lib/expense-api';

export type ApprovalDecision = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface ExpenseApproval {
  approvalId: number;
  reportId: number;
  entityId: number;
  approverUserOid: string;
  requesterUserOid: string;
  seq: number;
  decision: ApprovalDecision;
  decidedAt: string | null;
  comment: string | null;
}

const keys = {
  inbox: () => ['expense', 'approvals', 'inbox'] as const,
};

/** The caller's approval inbox (reports awaiting their decision). */
export function useApprovalInbox() {
  return useQuery({
    queryKey: keys.inbox(),
    queryFn: () =>
      expenseFetch<ExpenseApproval[]>('/v1/expense/approvals/inbox'),
  });
}

/** Approve or reject one routed approval. */
export function useDecideApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: {
      approvalId: number;
      decision: Exclude<ApprovalDecision, 'PENDING'>;
      comment?: string;
    }) =>
      expenseFetch(`/v1/expense/approvals/${req.approvalId}/decision`, {
        method: 'POST',
        body: JSON.stringify({ decision: req.decision, comment: req.comment }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['expense', 'approvals'] });
      qc.invalidateQueries({ queryKey: ['expense', 'reports'] });
    },
  });
}
